import { useMemo } from 'react';
import merge from 'lodash/merge';

import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeOptions, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

import { useDynamicPalette } from 'src/hooks/use-dynamic-palette';

import { useCustomContent } from 'src/stores/custom-content/custom-content.store';

import { useSettingsContext } from 'src/components/settings';

// system
import { shadows } from './shadows';
import { typography } from './typography';
// options
import RTL from './options/right-to-left';
import { customShadows } from './custom-shadows';
import { componentsOverrides } from './overrides';
import { createPresets } from './options/presets';
import { createContrast } from './options/contrast';

type Props = {
  children: React.ReactNode;
};

export interface CustomThemeOptions extends ThemeOptions {
  dynamicPalette: {
    mainBanners: {
      bgColor: string,
      textColor: string,
      buttonColor: string,
      buttonTextColor: string
    },
    primaryButtons: {
      bgColor: string,
      textColor: string
    },
    homePage: {
      bannerText: {
        specialColor: string
      }
    }
  }
}

export default function ThemeProvider({ children }: Props) {
  const settings = useSettingsContext();

  const { eventData } = useCustomContent((state) => state)

  const presets = createPresets(settings.themeColorPresets);

  const dynamicPalette = useDynamicPalette

  const contrast = createContrast(settings.themeContrast, settings.themeMode);

  const memoizedValue = useMemo(
    () => ({
      dynamicPalette: {
        ...dynamicPalette(settings.themeMode, eventData?.customContent)
      },
      palette: {
        ...dynamicPalette(settings.themeMode, eventData?.customContent),
        ...presets.palette,
        ...contrast.palette,
      },
      customShadows: {
        ...customShadows(settings.themeMode),
        ...presets.customShadows,
      },
      direction: settings.themeDirection,
      shadows: shadows(settings.themeMode),
      shape: { borderRadius: 8 },
      typography,
    }),
    [
      settings.themeMode,
      settings.themeDirection,
      presets.palette,
      presets.customShadows,
      contrast.palette,
    ]
  );

  const auxTheme = createTheme(memoizedValue as CustomThemeOptions );

  auxTheme.components = merge(componentsOverrides(auxTheme), contrast.components);

  return (
    <MuiThemeProvider theme={auxTheme}>
      <RTL themeDirection={settings.themeDirection}>
        <CssBaseline />
        {children}
      </RTL>
    </MuiThemeProvider>
  );
}
